import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { ApiError } from '../../shared/api/errors';
import { ClippedText } from '../../shared/ui/clipped-text';
import { DeleteIcon, EventsIcon } from '../../shared/ui/icons';
import { Page, PageHeader } from '../../shared/ui/layout';
import { ListFooter } from '../../shared/ui/list-footer';
import { MenuItem, MenuSeparator } from '../../shared/ui/menu';
import { CopyMenuItem, RowMenu } from '../../shared/ui/row-menu';
import { StatusBadge } from '../../shared/ui/status-badge';
import { Table, type IColumn } from '../../shared/ui/table';
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  LoadingState,
  NoMatchState,
} from '../../shared/ui/states';
import {
  counted,
  destinationHasProblems,
  endpointLabel,
  keepsDestination,
  lastAnswer,
  listDestinationsPage,
  useDestinationLiveUpdate,
  type IDestinationFilters,
  type TDestination,
} from '../../entities/destination';
import { EMPTY_RUN_FILTERS, runFiltersToSearch } from '../../entities/run';
import { destinationFlowCounts, usedInKeys } from '../../entities/used-in';
import { DestinationCreate } from '../../features/destination-create';
import { DestinationDelete } from '../../features/destination-delete';
import { DestinationFiltersBar } from './ui/filters-bar';

const LIVE_KEYS = [['destinations']];

const windowHint = (windowHours: number | null) =>
  windowHours
    ? `Counted over the past ${windowHours} hours and recomputed every few minutes; a dash means not counted yet, which is not the same as none.`
    : 'Recomputed every few minutes; a dash means not counted yet, which is not the same as none.';

// The Flows view of Events: what was delivered here, over the window its counts cover.
const eventsSearch = (destination: TDestination) =>
  runFiltersToSearch({
    ...EMPTY_RUN_FILTERS,
    minutes: destination.activity.windowHours * 60,
    show: 'all',
    destinationId: [destination.id],
  });

const Takers = ({ flows }: { flows: number | undefined }) => {
  if (flows === undefined) {
    return (
      <span className="text-faint" title="Not counted yet">
        —
      </span>
    );
  }
  return (
    <span className={flows === 0 ? 'text-faint' : undefined}>{flows.toLocaleString('en-US')}</span>
  );
};

/** A dash, not a zero: `computedAt: null` is "the rollup has not run". */
const Windowed = ({
  destination,
  field,
}: {
  destination: TDestination;
  field: 'deliveredInWindow' | 'retryingInWindow' | 'exhaustedInWindow';
}) => {
  const value = destination.activity
    ? counted(destination.activity[field], destination.activity.computedAt)
    : null;
  if (value === null) {
    return (
      <span className="text-faint" title="Not counted yet">
        —
      </span>
    );
  }
  return <>{value}</>;
};

// `flows` is null until counted.
const columns = (
  windowHours: number | null,
  flows: ReadonlyMap<string, number> | null,
  onDelete: (destination: TDestination) => void,
): IColumn<TDestination>[] => [
  {
    key: 'state',
    header: 'Status',
    width: 'w-col-status',
    ui: true,
    render: (destination) => <StatusBadge vocabulary="destination" value={destination.state} />,
  },
  {
    key: 'name',
    header: 'Destination',
    width: 'grow',
    ui: true,
    render: (destination) => <ClippedText text={destination.name} lines={2} />,
  },
  {
    key: 'url',
    header: 'Endpoint',
    width: 'grow',
    // Wraps to a second line rather than cutting: at 1280 a middle ellipsis ate the host.
    render: (destination) => (
      <ClippedText text={endpointLabel(destination)} lines={2} className="font-mono" />
    ),
  },
  {
    key: 'signing',
    header: 'Signed',
    width: 'w-col-try',
    ui: true,
    render: (destination) =>
      destination.transport !== 'http' ? (
        <span className="text-faint">—</span>
      ) : destination.config.signOutbound === true && destination.secretSet ? (
        'yes'
      ) : (
        <span className="text-faint">no</span>
      ),
  },
  {
    key: 'flows',
    header: 'Flows',
    width: 'w-col-time',
    align: 'right',
    hint: 'Live flows with a step delivering here. Which ones: on the destination.',
    render: (destination) => (
      <Takers flows={flows ? (flows.get(destination.id) ?? 0) : undefined} />
    ),
  },
  {
    key: 'answer',
    header: 'Last answer',
    width: 'w-col-when',
    align: 'right',
    render: (destination) => {
      const answer = lastAnswer(destination.activity);
      return answer === null ? <span className="text-faint">—</span> : answer;
    },
  },
  {
    key: 'delivered',
    header: 'Delivered',
    width: 'w-col-when',
    align: 'right',
    hint: windowHint(windowHours),
    render: (destination) => <Windowed destination={destination} field="deliveredInWindow" />,
  },
  {
    key: 'retrying',
    header: 'Retry pending',
    width: 'w-col-time',
    align: 'right',
    hint: windowHint(windowHours),
    render: (destination) => <Windowed destination={destination} field="retryingInWindow" />,
  },
  {
    key: 'exhausted',
    header: 'Gave up',
    width: 'w-col-time',
    align: 'right',
    hint: `Deliveries that failed for good: every try the policy allowed, or an answer not worth retrying (a 400 fails on its first). ${windowHint(windowHours)}`,
    render: (destination) => <Windowed destination={destination} field="exhaustedInWindow" />,
  },
  {
    key: 'menu',
    header: '',
    width: 'w-col-row-menu',
    ui: true,
    render: (destination) => (
      <RowMenu label={destination.name}>
        {destination.transport === 'http' && destination.config.url && (
          <CopyMenuItem value={destination.config.url}>Copy endpoint</CopyMenuItem>
        )}
        <MenuItem asChild>
          <Link to="/events" search={eventsSearch(destination)}>
            <EventsIcon />
            See its events
          </Link>
        </MenuItem>
        <MenuSeparator />
        <MenuItem onSelect={() => onDelete(destination)} className="text-danger-text">
          <DeleteIcon />
          Delete…
        </MenuItem>
      </RowMenu>
    ),
  },
];

interface IDestinationsPageProps {
  filters: IDestinationFilters;
  onFilters: (next: IDestinationFilters) => void;
  onOpen: (destination: TDestination) => void;
}

const noFiltersActive = (filters: IDestinationFilters): boolean =>
  !filters.health && !filters.used && !filters.hasProblems && !filters.q;

export const DestinationsPage = ({ filters, onFilters, onOpen }: IDestinationsPageProps) => {
  const destinations = useQuery({
    queryKey: ['destinations', 'list', filters.q ?? null, filters.cursor ?? null],
    queryFn: () => listDestinationsPage({ q: filters.q, cursor: filters.cursor }),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
  });
  useDestinationLiveUpdate(LIVE_KEYS);
  const [deleting, setDeleting] = useState<TDestination | null>(null);

  const all = useMemo(() => destinations.data?.data ?? [], [destinations.data]);
  const rows = all.filter((destination) => keepsDestination(destination, filters));
  const windowHours = all[0]?.activity?.windowHours ?? null;
  const flowCounts = useQuery({
    queryKey: usedInKeys.destinationCounts,
    queryFn: destinationFlowCounts,
  });
  const tableColumns = useMemo(
    () => columns(windowHours, flowCounts.data ?? null, setDeleting),
    [windowHours, flowCounts.data],
  );

  // A count from one page of several would understate; only a whole list is counted.
  const wholeList = !filters.cursor && !destinations.data?.nextCursor;
  const hasProblemsCount =
    destinations.isSuccess && wholeList
      ? all.filter(
          (destination) =>
            keepsDestination(destination, { ...filters, hasProblems: undefined }) &&
            destinationHasProblems(destination),
        ).length
      : undefined;

  const isForbidden =
    destinations.isError &&
    destinations.error instanceof ApiError &&
    destinations.error.status === 403;
  const nothingExists = destinations.isSuccess && all.length === 0 && noFiltersActive(filters);
  const noMatch = destinations.isSuccess && !nothingExists && rows.length === 0;

  return (
    <>
      <PageHeader
        title="Destinations"
        description="Where flows deliver, what we sign it with, and how hard we push."
        actions={<DestinationCreate onCreated={onOpen} />}
      />

      {!nothingExists && (
        <DestinationFiltersBar
          filters={filters}
          onFilters={onFilters}
          hasProblemsCount={hasProblemsCount}
        />
      )}

      <Page width="full" className="flex-1">
        <div className="flex flex-col gap-section-gap">
          {destinations.isPending && <LoadingState rows={6} />}
          {destinations.isError && isForbidden && <ForbiddenState />}
          {destinations.isError && !isForbidden && (
            <ErrorState error={destinations.error} onRetry={() => void destinations.refetch()} />
          )}

          {nothingExists && (
            <EmptyState
              title="No destinations yet"
              description="A destination is where a flow sends what it delivers: an HTTP endpoint, or your own machine through the CLI. Nothing is delivered anywhere until one exists and a flow uses it."
              action={<DestinationCreate onCreated={onOpen} label="Create the first one" />}
            />
          )}

          {noMatch && <NoMatchState onReset={() => onFilters({})} />}

          {rows.length > 0 && (
            <>
              <Table
                tall
                layoutId="destinations"
                columns={tableColumns}
                rows={rows}
                rowKey={(row) => row.id}
                muted={(row) => row.state === 'unused'}
                onRowClick={onOpen}
              />
              <ListFooter
                note={
                  windowHours
                    ? `Last ${windowHours} hours. Retry pending is live; Gave up means every try the policy allowed failed. Flows = live flows delivering here.`
                    : 'Retry pending is live; Gave up means every try the policy allowed failed. Flows = live flows delivering here.'
                }
                shown={rows.length}
                total={destinations.data?.total}
                onNext={
                  destinations.data?.nextCursor
                    ? () =>
                        onFilters({
                          ...filters,
                          cursor: destinations.data?.nextCursor ?? undefined,
                        })
                    : undefined
                }
              />
            </>
          )}
        </div>
      </Page>

      {deleting && (
        <DestinationDelete
          destination={deleting}
          open
          onOpenChange={(open) => !open && setDeleting(null)}
        />
      )}
    </>
  );
};
